import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoContentHygiene, seoContentHygieneAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Stat, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/content-hygiene")({
  head: () => ({ meta: [{ title: "Content hygiene · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: HygienePage,
});

function HygienePage() {
  const load = useServerFn(seoContentHygiene);
  const act = useServerFn(seoContentHygieneAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-hygiene"], queryFn: () => load() });
  const d = q.data as any;
  const images = (d?.images ?? []) as any[];
  const decay = (d?.decay ?? []) as any[];
  const thin = (d?.thin ?? []) as any[];

  const run = useMutation({
    mutationFn: (action: "alt_audit" | "refresh_decay") => act({ data: { action } }),
    onSuccess: (r: any) => {
      toast.success(
        typeof r.issues === "number"
          ? `Checked ${r.pagesAudited} pages — ${r.issues} images need descriptions.`
          : r.state === "configured_no_ingestion"
            ? "No Search Console data yet, so nothing can be compared."
            : `${r.flagged} pages losing traffic.`,
      );
      qc.invalidateQueries({ queryKey: ["seo-hygiene"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "That check could not run."),
  });

  const resolve = useMutation({
    mutationFn: (id: string) => act({ data: { action: "resolve_image", id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo-hygiene"] }),
  });

  return (
    <SeoPage
      title="Content hygiene"
      description="Images with no description, pages too thin to rank, and pages that used to get visitors and no longer do."
      actions={
        <>
          <Button variant="outline" onClick={() => run.mutate("alt_audit")} disabled={run.isPending}>
            Check image descriptions
          </Button>
          <Button onClick={() => run.mutate("refresh_decay")} disabled={run.isPending}>
            Check for falling pages
          </Button>
        </>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : !d?.hasCrawl ? (
        <Empty>No site crawl has run yet, so there is nothing to inspect.</Empty>
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Images missing a description" value={num(images.length)} />
            <Stat label="Thin pages" value={num(thin.length)} />
            <Stat label="Pages losing traffic" value={num(decay.length)} />
          </div>

          <Panel title="Images without a description" subtitle="Screen readers and image search both rely on this text. A suggested description is offered from the page heading and file name.">
            {images.length === 0 ? (
              <Empty>Every image found has a description.</Empty>
            ) : (
              <DataTable
                head={["Page", "Image", "Issue", "Suggested description", ""]}
                rows={images.map((r) => [
                  r.path,
                  <span key={r.id} className="break-all text-xs">{r.image_src}</span>,
                  r.issue === "missing_alt" ? "No description" : "Empty description",
                  r.suggested_alt,
                  <Button key={`${r.id}-b`} size="sm" variant="outline" onClick={() => resolve.mutate(r.id)}>
                    Mark done
                  </Button>,
                ])}
              />
            )}
          </Panel>

          <Panel title="Thin pages" subtitle="Under 250 words of visible content — usually too little to rank for anything competitive.">
            <DataTable head={["Page", "Words", "Links in"]} rows={thin.map((t) => [t.path, num(t.wordCount), num(t.inbound)])} />
          </Panel>

          <Panel title="Pages losing traffic" subtitle="Last 28 days against the 28 before, from Search Console only.">
            {decay.length === 0 ? (
              <Empty>No decline detected — or no Search Console data collected yet.</Empty>
            ) : (
              <DataTable
                head={["Page", "Severity", "Clicks now", "Clicks before", "Position now", "Why"]}
                rows={decay.map((r) => [
                  r.path,
                  <Pill key={r.id} tone={r.severity === "critical" ? "bad" : "warn"}>{r.severity}</Pill>,
                  num(Number(r.clicks_current)),
                  num(Number(r.clicks_previous)),
                  Number(r.position_current).toFixed(1),
                  (r.reasons ?? []).join("; "),
                ])}
              />
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
